/**
 * Vehicles — fleet table.
 *
 * Sprint I delivers the data foundation and a minimal table view; the
 * full Live Dispatch Map (figma-scope-v2 wow-screen #2) is a separate
 * sprint that builds on this page's API client and event subscriptions.
 *
 * Design: density-first table. Status badges, coords as inline mono,
 * last-seen as relative time. One-click status drawer (Idle / Busy /
 * Maintenance) when the user has `vehicle.dispatch`.
 */

import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, MapPin, RefreshCw, Truck, WifiOff, Wrench,
} from "lucide-react";
import { Badge, Button, Card, CardHeader, cn } from "@vseh/ui";

import {
  vehiclesApi,
  type VehicleDto, type VehicleStatus,
} from "../shared/api";
import { useEventStream, useFetch, type EventEnvelope } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { EmptyBlock, ErrorBlock } from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";


const STATUS_FILTERS: { value: VehicleStatus | "all"; labelKey: string }[] = [
  { value: "all",         labelKey: "vehicles.filter.all" },
  { value: "idle",        labelKey: "vehicles.filter.idle" },
  { value: "busy",        labelKey: "vehicles.filter.busy" },
  { value: "offline",     labelKey: "vehicles.filter.offline" },
  { value: "maintenance", labelKey: "vehicles.filter.maintenance" },
];


export function VehiclesPage(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<VehicleStatus | "all">("all");

  const fetcher = useCallback(
    (signal: AbortSignal) => vehiclesApi.list(
      filter === "all" ? {} : { status: filter },
      { signal },
    ),
    [filter],
  );
  const { data, error, loading, refetch } = useFetch<VehicleDto[]>(
    fetcher, [filter],
  );

  // Live updates — coords + status come through ws-gateway. We just
  // re-fetch on each relevant event (cheap at fleet scale). A future
  // sprint will merge per-vehicle deltas in place to avoid the round-trip.
  const onEvent = useCallback((env: EventEnvelope) => {
    if (env.event_type?.startsWith("vehicle.")) refetch();
  }, [refetch]);
  useEventStream(onEvent, { subjects: ["vehicle.>"] });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={t("vehicles.title")}
          description={t("vehicles.subtitle")}
          actions={
            <Button variant="ghost" size="xs"
                    leftIcon={<RefreshCw className="size-3.5" />}
                    onClick={refetch}>
              {t("common.refresh")}
            </Button>
          }
        />
        <StatusFilterRow value={filter} onChange={setFilter} />

        {loading && !data && <SkeletonTable rows={8} cols={7} />}
        {error && <ErrorBlock error={error} onRetry={refetch} />}

        {data && data.length === 0 && !loading && (
          <EmptyBlock
            title={t("vehicles.empty.title")}
            description={t("vehicles.empty.description")}
          />
        )}

        {data && data.length > 0 && (
          <VehicleTable rows={data} onStatusChanged={refetch} />
        )}
      </Card>
    </div>
  );
}


function StatusFilterRow(props: {
  value: VehicleStatus | "all";
  onChange: (v: VehicleStatus | "all") => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="mb-3 flex items-center gap-1 rounded-sm border border-base-200 bg-base-50 p-0.5">
      {STATUS_FILTERS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => props.onChange(opt.value)}
          className={cn(
            "rounded-sm px-2 py-0.5 text-2xs font-medium transition-colors",
            props.value === opt.value
              ? "bg-primary-900 text-white"
              : "text-base-700 hover:bg-base-100",
          )}
        >
          {t(opt.labelKey)}
        </button>
      ))}
    </div>
  );
}


function VehicleTable({
  rows, onStatusChanged,
}: { rows: VehicleDto[]; onStatusChanged: () => void }): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b border-base-200 text-2xs uppercase tracking-wide text-base-500">
          <tr>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.vehicle")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.type")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.plate")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.coords")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.speed")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.last_ping")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("vehicles.col.status")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <VehicleRow
              key={v.id} vehicle={v} onStatusChanged={onStatusChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function VehicleRow({
  vehicle, onStatusChanged,
}: { vehicle: VehicleDto; onStatusChanged: () => void }): JSX.Element {
  const t = useT();
  const [updating, setUpdating] = useState(false);

  const flipStatus = useCallback(async (next: VehicleStatus) => {
    if (updating) return;
    setUpdating(true);
    try {
      await vehiclesApi.updateStatus(vehicle.id, { status: next });
      onStatusChanged();
    } finally {
      setUpdating(false);
    }
  }, [vehicle.id, onStatusChanged, updating]);

  return (
    <tr className="border-b border-base-100 align-top hover:bg-base-50">
      <td className="py-1.5 pr-3">
        <div className="flex items-center gap-1.5">
          <Truck className="size-3.5 text-base-500" aria-hidden="true" />
          <span className="font-medium text-base-900">{vehicle.display_name}</span>
        </div>
      </td>
      <td className="py-1.5 pr-3 text-base-700">{vehicle.vehicle_type}</td>
      <td className="py-1.5 pr-3 font-mono text-base-700">
        {vehicle.license_plate ?? <span className="text-base-400">—</span>}
      </td>
      <td className="py-1.5 pr-3 font-mono text-base-700 tabular-nums">
        {vehicle.current_lat != null && vehicle.current_lng != null ? (
          <span className="flex items-center gap-1">
            <MapPin className="size-3 text-ai-500" aria-hidden="true" />
            {vehicle.current_lat.toFixed(4)}, {vehicle.current_lng.toFixed(4)}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-base-400">
            <WifiOff className="size-3" aria-hidden="true" />
            {t("vehicles.empty.short")}
          </span>
        )}
      </td>
      <td className="py-1.5 pr-3 text-base-700 tabular-nums">
        {vehicle.current_speed_kmh != null
          ? `${Math.round(vehicle.current_speed_kmh)} ${t("vehicles.unit.kmh")}`
          : <span className="text-base-400">—</span>}
      </td>
      <td className="py-1.5 pr-3 text-base-700 tabular-nums">
        {vehicle.last_seen_at ? <RelativeTime iso={vehicle.last_seen_at} /> : "—"}
      </td>
      <td className="py-1.5 pr-3">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={vehicle.status} />
          {/* Quick flip controls. Render in disabled state when API
              call is in flight so we don't double-submit. */}
          <StatusToggle
            current={vehicle.status}
            onChange={flipStatus}
            disabled={updating}
          />
        </div>
      </td>
    </tr>
  );
}


function StatusBadge({ status }: { status: VehicleStatus }): JSX.Element {
  const tone = ({
    idle:        "neutral" as const,
    busy:        "ai"      as const,
    offline:     "danger"  as const,
    maintenance: "heritage" as const,
  }[status] ?? "neutral");
  const icon = status === "maintenance" ? <Wrench className="mr-1 size-3" />
              : status === "offline"     ? <AlertTriangle className="mr-1 size-3" />
              : null;
  return (
    <Badge tone={tone} dot>
      {icon}
      {status}
    </Badge>
  );
}


function StatusToggle(props: {
  current: VehicleStatus;
  onChange: (next: VehicleStatus) => void;
  disabled?: boolean;
}): JSX.Element {
  // We deliberately omit `offline` from the operator's quick-toggle —
  // offline is what the telematics worker writes when pings stop;
  // dispatcher shouldn't force a vehicle offline manually.
  const options: VehicleStatus[] = ["idle", "busy", "maintenance"];
  return (
    <div className="flex items-center gap-0.5">
      {options.filter(o => o !== props.current).map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => props.onChange(o)}
          disabled={props.disabled}
          className={cn(
            "rounded-sm px-1.5 py-0.5 text-2xs font-medium",
            "text-base-600 hover:bg-base-100 disabled:opacity-50",
          )}
        >
          → {o}
        </button>
      ))}
    </div>
  );
}


function RelativeTime({ iso }: { iso: string }): JSX.Element {
  const t = useT();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);
  // tick triggers re-render every 15s; the formatRelative call below
  // re-reads Date.now() so the label stays fresh.
  void tick;
  const label = formatRelative(iso, t);
  return <span>{label}</span>;
}


function formatRelative(iso: string, t: (k: string, p?: Record<string, string | number>) => string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return t("vehicles.ago.just_now");
  const s = Math.round(ms / 1000);
  if (s < 30) return t("vehicles.ago.just_now");
  if (s < 60) return t("vehicles.ago.seconds", { s });
  const m = Math.round(s / 60);
  if (m < 60) return t("vehicles.ago.minutes", { m });
  const h = Math.round(m / 60);
  if (h < 24) return t("vehicles.ago.hours", { h });
  const d = Math.round(h / 24);
  return t("vehicles.ago.days", { d });
}
